import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import { authService } from './auth.service'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })
        
        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}

// ============================================================================
// Type Definitions
// ============================================================================

export interface EnrollmentDetails {
    id: string
    user_id: string
    user_email: string
    user_username: string
    user_first_name: string | null
    user_last_name: string | null
    product_id: string
    product_type: 'course' | 'bundle' | 'digital_product'
    product_title: string
    purchase_price: number
    currency: string
    enrolled_at: string
    access_expires_at?: string
    status: 'active' | 'expired' | 'refunded' | 'cancelled'
    progress_percentage: number
    certificate_issued: boolean
    completed_at?: string
    last_accessed_at?: string
    created_at: string
    updated_at: string
}

export interface SearchFilters {
    user_id?: string
    enrollment_id?: string
    username?: string
    email?: string
    product_id?: string
    status?: string
    search_query?: string
}

export interface EnrollmentsStats {
    total_enrollments: number
    active_enrollments: number
    completed_enrollments: number
    total_revenue: number
}

// ============================================================================
// Service Class
// ============================================================================

class AdminEnrollmentsService {
    /**
     * Validate authentication token exists
     */
    private validateAuth(): void {
        if (!authService.isAuthenticated()) {
            throw new Error('AUTHENTICATION_REQUIRED: No authentication token found. Please log in.')
        }
    }

    /**
     * Handle authentication failures by logging out user
     */
    private handleAuthenticationFailure(error: any): void {
        const errorMessage = error.message || String(error)
        
        if (
            errorMessage.includes('Could not validate credentials') ||
            errorMessage.includes('Unauthorized') ||
            errorMessage.includes('Invalid token') ||
            errorMessage.includes('Token expired') ||
            errorMessage.includes('AUTHENTICATION_REQUIRED') ||
            error.response?.status === 401
        ) {
            console.error('Authentication failure detected. Logging out user...')
            
            setTimeout(() => {
                authService.logout()
            }, 1000)
            
            throw new Error('Your session has expired. Please log in again.')
        }
    }

    /**
     * Search enrollments with flexible filters
     */
    async searchEnrollments(filters: SearchFilters): Promise<ApiResponse<EnrollmentDetails[]>> {
        try {
            this.validateAuth()
            
            // Build query params from filters
            const params: any = {}
            if (filters.user_id) params.user_id = filters.user_id
            if (filters.enrollment_id) params.enrollment_id = filters.enrollment_id
            if (filters.username) params.username = filters.username
            if (filters.email) params.email = filters.email
            if (filters.product_id) params.product_id = filters.product_id
            if (filters.status) params.status = filters.status
            if (filters.search_query) params.search_query = filters.search_query
            
            const response = await getApiInstance().get('/admin/enrollments/search', {
                params
            })
            
            const enrollments = response.data?.data || response.data || []
            
            return {
                success: true,
                data: enrollments
            }
        } catch (error: any) {
            console.error('Error searching enrollments:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to search enrollments'
            }
        }
    }

    /**
     * Get all enrollments with pagination
     */
    async getAllEnrollments(page: number = 1, pageSize: number = 50): Promise<ApiResponse<EnrollmentDetails[]>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get('/admin/enrollments', {
                params: { page, page_size: pageSize }
            })
            
            const enrollments = response.data?.data || response.data || []
            
            return {
                success: true,
                data: enrollments
            }
        } catch (error: any) {
            console.error('Error fetching enrollments:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch enrollments'
            }
        }
    }

    /**
     * Get enrollments statistics
     */
    async getEnrollmentsStats(): Promise<ApiResponse<EnrollmentsStats>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get('/admin/enrollments/stats')
            
            const stats = response.data?.data || response.data
            
            return {
                success: true,
                data: stats
            }
        } catch (error: any) {
            console.error('Error fetching enrollment stats:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch enrollment statistics'
            }
        }
    }

    /**
     * Get single enrollment details
     */
    async getEnrollmentDetails(enrollmentId: string): Promise<ApiResponse<EnrollmentDetails>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().get(`/admin/enrollments/${enrollmentId}`)
            
            const enrollment = response.data?.data || response.data
            
            return {
                success: true,
                data: enrollment
            }
        } catch (error: any) {
            console.error('Error fetching enrollment details:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch enrollment details'
            }
        }
    }

    /**
     * Update enrollment status
     */
    async updateEnrollmentStatus(
        enrollmentId: string, 
        status: 'active' | 'expired' | 'refunded' | 'cancelled'
    ): Promise<ApiResponse<EnrollmentDetails>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().patch(
                `/admin/enrollments/${enrollmentId}/status`,
                { status }
            )
            
            const enrollment = response.data?.data || response.data
            
            return {
                success: true,
                data: enrollment
            }
        } catch (error: any) {
            console.error('Error updating enrollment status:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to update enrollment status'
            }
        }
    }

    /**
     * Update enrollment details
     */
    async updateEnrollment(
        enrollmentId: string, 
        updates: Partial<EnrollmentDetails>
    ): Promise<ApiResponse<EnrollmentDetails>> {
        try {
            this.validateAuth()
            
            const response = await getApiInstance().patch(
                `/admin/enrollments/${enrollmentId}`,
                updates
            )
            
            const enrollment = response.data?.data || response.data
            
            return {
                success: true,
                data: enrollment
            }
        } catch (error: any) {
            console.error('Error updating enrollment:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to update enrollment'
            }
        }
    }

    /**
     * Delete enrollment
     */
    async deleteEnrollment(enrollmentId: string): Promise<ApiResponse<null>> {
        try {
            this.validateAuth()
            
            await getApiInstance().delete(`/admin/enrollments/${enrollmentId}`)
            
            return {
                success: true,
                data: null
            }
        } catch (error: any) {
            console.error('Error deleting enrollment:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to delete enrollment'
            }
        }
    }

    /**
     * Batch delete multiple enrollments
     */
    async batchDeleteEnrollments(enrollmentIds: string[]): Promise<ApiResponse<{
        deleted_count: number,
        failed_count: number,
        failed_ids: string[]
    }>> {
        try {
            this.validateAuth()
            
            if (!enrollmentIds || enrollmentIds.length === 0) {
                return {
                    success: false,
                    error: 'No enrollment IDs provided'
                }
            }
            
            const response = await getApiInstance().post('/admin/enrollments/batch-delete', {
                enrollment_ids: enrollmentIds
            })
            
            const result = response.data?.data || {
                deleted_count: 0,
                failed_count: 0,
                failed_ids: []
            }
            
            return {
                success: true,
                data: result
            }
        } catch (error: any) {
            console.error('Error batch deleting enrollments:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to batch delete enrollments'
            }
        }
    }

    /**
     * Get enrollments for a specific user
     */
    async getUserEnrollments(userId: string): Promise<ApiResponse<EnrollmentDetails[]>> {
        try {
            this.validateAuth()
            
            const response = await this.searchEnrollments({ user_id: userId })
            
            return response
        } catch (error: any) {
            console.error('Error fetching user enrollments:', error)
            this.handleAuthenticationFailure(error)
            return {
                success: false,
                error: error.response?.data?.detail || error.message || 'Failed to fetch user enrollments'
            }
        }
    }
}

// Export singleton instance
export const adminEnrollmentsService = new AdminEnrollmentsService()
export default adminEnrollmentsService
